import React from "react";
import { Link } from "react-router-dom";
import { SITE_CONTENT } from "../../constants/siteContent";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import Section, { SectionHead } from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import styles from "./ContentPage.module.css";

/*
 * ContentPage — the template behind ~30 routes (every service, industry,
 * company, pricing and legal page in SITE_CONTENT.pages, plus the 404).
 *
 * It renders ONLY what a page actually has. Every block below is optional and
 * driven by a key on the page object: intro, sections, team, serviceHighlights,
 * differentiators, process, technologyGroups, pageFaqs. Nothing is invented to
 * fill a slot. The 404 hands in a minimal object (eyebrow / title / subtitle /
 * sections, no path) and takes the same route through this file.
 *
 * Design rules it follows (same as every homepage section):
 *   • <Section> owns surface + vertical rhythm + container — for EVERY block,
 *     the hero included. This module never sets a background, a padding-block
 *     or a container width on a <section>.
 *   • Surfaces: paper hero -> paper body -> olive PANEL for the process band ->
 *     paper -> olive PANEL closing CTA. Neighbouring paper sections read as one
 *     sheet (Section halves the joint), so there is no tone cycle to maintain.
 *   • The site header is fixed and transparent with no bar of its own, so the
 *     hero reserves var(--header-h) above its copy (.hero) — the wordmark can
 *     never sit on the page h1.
 *   • The h1 is a <Statement>. page.title is DATA, so it is rendered as-is —
 *     no serif <em> is guessed into a string this file cannot read.
 *   • <SectionHead> is the only h2 block. Its <h2> is animated globally by
 *     HeadingWipe (App.js) — never animated here, never wrapped in <Reveal>.
 *   • Tokens only, no raw colours, no gradients, no shadows.
 *   • Repeated CTAs use the .link-arrow primitive; at most one lime .btn per
 *     section.
 *
 * THREE THINGS THAT WERE DELIBERATELY REMOVED — please do not put them back:
 *
 * 1. TECH_LOGO_MAP hotlinked 23 vendor logos from cdn.simpleicons.org and
 *    cdn.jsdelivr.net. That put a third-party CDN in the critical path of every
 *    service page, leaked a referrer to two external hosts on every visit, and
 *    made render latency someone else's problem. Technology names are now set
 *    as typography, exactly like the homepage stack row does it. No generic
 *    glyph is substituted for a vendor's brand mark either — a shipped icon
 *    standing in for a real logo would misrepresent that brand. This file
 *    makes ZERO external network requests.
 *
 * 2. One photo for fifteen routes. PAGE_IMAGE pointed /about, /careers,
 *    /insights, /faq, /privacy-policy, /terms, /sitemap and eight more at the
 *    same company.jpg. Every image in public/images/pages is now used at most
 *    once (see the map below), and a route with no honest match gets a
 *    typographic hero instead of filler. Legal and navigation pages carry no
 *    decorative hero at all.
 *
 * 3. Card-in-a-box everything. Sections, highlights, FAQs and related links
 *    were eight variants of "rounded box with a shadow". They are hairline-
 *    topped editorial cells now, with the notched <TicketCard> reserved for the
 *    two blocks that really are cards (highlights, onward links).
 */

/* ---------------------------------------------------------------- imagery --
 * ONE IMAGE, ONE ROUTE. The filenames in public/images/pages are historical and
 * several of them lie about their contents (support.jpg is a clinician,
 * devops.jpg is a piggy bank, travel-logistics.jpg is a handshake), so every
 * entry below was assigned by looking at the actual photograph, not the name.
 *
 * A route only appears here when a genuinely fitting photo exists. Everything
 * else — /industries, /cloud-modernization, /cybersecurity, /compliance,
 * /managed-support, /leadership, /client-reviews, /engagement-model, /faq,
 * /corporate-social-responsibility, the three remaining industry pages, the
 * legal pages and the 404 — renders the typographic hero. That is the correct
 * outcome: a strong headline beats a stock photo that means nothing.
 *
 * public/images/pages/support.jpg is intentionally unused. It is a portrait
 * crop of a clinician and duplicates healthcare.jpg.
 *
 * TODO(content): when Appinfigo has its own photography, add the route here.
 * Keep the rule — one image, one route — and write a real alt description.
 */
const PAGE_IMAGE = {
  "/services": {
    src: "/images/pages/web-design-new-3.jpg",
    alt: "A close-up of a code editor showing a stylesheet and its project file tree"
  },
  "/product-engineering": {
    src: "/images/pages/web-design-new-1.jpg",
    alt: "Two desktop displays on a studio desk showing product page layouts"
  },
  "/web-development-services": {
    src: "/images/pages/web-design-new-2.jpg",
    alt: "A laptop screen filled with two side-by-side panes of source code"
  },
  "/mobile-app-development": {
    src: "/images/pages/web-design-new-5.jpg",
    alt: "A developer checking an app on a phone beside a laptop running a mobile IDE"
  },
  "/ui-ux-design-services": {
    src: "/images/pages/web-design-new-4.jpg",
    alt: "A notebook of hand-drawn interface wireframes with two pens resting on it"
  },
  "/data-and-ai": {
    src: "/images/pages/mobile-app.jpg",
    alt: "Lines of code and data projected across a person standing in a dark room"
  },
  "/cloud-services": {
    src: "/images/pages/cloud.jpg",
    alt: "A monitor showing terminal panes of system, network and process monitoring"
  },
  "/devops-services": {
    src: "/images/pages/web-development.jpg",
    alt: "A person holding a book about Python for Unix and Linux system administration"
  },
  "/healthcare-software-solutions": {
    src: "/images/pages/healthcare.jpg",
    alt: "A clinician writing case notes at a desk beside a laptop"
  },
  "/fintech-banking-solutions": {
    src: "/images/pages/fintech.jpg",
    alt: "Two colleagues working through figures on a calculator and a laptop in an office"
  },
  "/about": {
    src: "/images/pages/public-sector.jpg",
    alt: "Overhead view of a team meeting around a wooden table as two people shake hands"
  },
  "/careers": {
    /* Was real-estate.jpg: a stock frame with "85.00%" repeated across five
       device screens, which reads as a fabricated metric on a careers page. */
    src: "/images/pages/web-design-new-2.jpg",
    alt: "A laptop screen showing source code in an editor"
  },
  "/partners": {
    src: "/images/pages/travel-logistics.jpg",
    alt: "Two people shaking hands across a desk at the end of a meeting"
  },
  "/pricing": {
    src: "/images/pages/devops.jpg",
    alt: "A piggy bank surrounded by loose coins from several currencies"
  },
  "/insights": {
    src: "/images/pages/ui-ux.jpg",
    alt: "A printed bar chart on a desk with a pen resting across it"
  },
  "/case-studies": {
    /* A real Appinfigo-built product, not stock. The previous image was a
       printout of Morris/Sparkline/Easy-Pie LIBRARY DEMO charts described as
       "result charts" — sample output presented as our results. */
    src: "/images/projects/topsel-trucking.jpg",
    alt: "Topsel Trucking, a logistics portal built by Appinfigo, shown live"
  }
};

/* Legal and navigation routes. They get no decorative hero, no marketing CTA in
   the hero, no "where to go next" grid and no closing CTA panel — a privacy
   policy is a document, not a landing page. */
const UTILITY_PATHS = ["/privacy-policy", "/terms", "/sitemap"];

/* TODO(content): section labels. These are structural (they name a block of the
   page), never claims — no metric, award, client or capability is asserted
   here. Copy that makes a claim lives in SITE_CONTENT. The <em> marks the one
   word of each label that sets in the serif accent; the words themselves are
   unchanged. */
const LABELS = {
  team: {
    eyebrow: "The team",
    title: (
      <>
        The people building your <em>product</em>
      </>
    )
  },
  highlights: {
    eyebrow: "In practice",
    title: (
      <>
        What this looks like in <em>practice</em>
      </>
    )
  },
  differentiators: {
    eyebrow: "Why us",
    title: (
      <>
        What makes the work <em>different</em>
      </>
    )
  },
  process: {
    eyebrow: "How we work",
    title: (
      <>
        How an engagement <em>runs</em>
      </>
    )
  },
  technology: {
    eyebrow: "Our stack",
    title: (
      <>
        The boring, proven tools we <em>build</em> with
      </>
    )
  },
  faqs: {
    eyebrow: "FAQ",
    title: (
      <>
        Questions we get <em>asked</em>
      </>
    )
  },
  related: {
    eyebrow: "Keep reading",
    title: (
      <>
        Where to go <em>next</em>
      </>
    )
  },
  cta: { eyebrow: "Next step" }
};

// TODO(content): hero + onward-link button labels
const HERO_CTA_LABEL = "Start a conversation";
const RELATED_CTA_LABEL = "Explore";

const PROMO = SITE_CONTENT.promoBar || {};
const SECONDARY_LINK = {
  label: PROMO.ctaLabel || "See live work",
  path: PROMO.ctaPath || "/case-studies"
};
const SECONDARY_FALLBACK = { label: "Explore services", path: "/services" };

/* CRA public asset URL. public/ images must be referenced through PUBLIC_URL
   from JS — never as url("/images/...") in a CSS module, which css-loader tries
   to resolve as a module and fails the build. */
function publicAsset(path) {
  return `${process.env.PUBLIC_URL}${path}`;
}

/* Stagger capped at four steps, so a nine-question FAQ does not crawl in. */
function stagger(index) {
  return Math.min(index, 3) * 60;
}

/* "01", "02" … a visual ordering device, always aria-hidden. */
function ordinal(index) {
  return String(index + 1).padStart(2, "0");
}

/* Up to three onward links. Same-eyebrow pages first (a service page points at
   services), then anything else in content order. Utility pages and /contact
   are excluded: the sitemap already indexes them and the CTA panel already
   handles contact. */
function getRelatedPages(page) {
  const candidates = SITE_CONTENT.pages.filter(
    (item) =>
      item.path &&
      item.path !== page.path &&
      item.path !== "/contact" &&
      UTILITY_PATHS.indexOf(item.path) === -1
  );
  const sameFamily = page.eyebrow
    ? candidates.filter((item) => item.eyebrow === page.eyebrow)
    : [];
  const rest = candidates.filter((item) => sameFamily.indexOf(item) === -1);

  return sameFamily.concat(rest).slice(0, 3);
}

/* Two ruled blocks share one skeleton: the page's own sections, and its FAQs.
   Heading left, body right, a hairline over every row — a shape that reads the
   same whether a page has three rows or nine, with no dangling half-empty grid
   row. It is also how the pricing page lists its price bands, so the heading
   column is sized to hold "MVP WEB APP — ₹1.5L TO ₹6L" without breaking the
   figure, and the row stacks to one column on a phone. */
function Ledger({ items, headingKey, bodyKey }) {
  return (
    <ul className={styles.ledger}>
      {items.map((item, index) => (
        <Reveal
          as="li"
          key={`${item[headingKey]}-${index}`}
          className={styles.ledgerRow}
          direction="up"
          delay={stagger(index)}
        >
          <h3 className={styles.ledgerHeading}>{item[headingKey]}</h3>
          <p className={styles.ledgerBody}>{item[bodyKey]}</p>
        </Reveal>
      ))}
    </ul>
  );
}

/* A row of notched ticket cards — highlights and the onward links. Column count
   comes from the item count, so three items make three columns and four make
   four. */
function Columns({ count, children }) {
  return (
    <ul
      className={styles.columns}
      style={{ "--cols": Math.min(Math.max(count, 1), 4) }}
    >
      {children}
    </ul>
  );
}

function ContentPage({ page }) {
  const intro = page.intro || [];
  const sections = page.sections || [];
  const heroImage = page.path ? PAGE_IMAGE[page.path] : null;
  const isUtility = Boolean(page.path) && UTILITY_PATHS.indexOf(page.path) !== -1;
  const related = isUtility ? [] : getRelatedPages(page);
  const secondary =
    page.path === SECONDARY_LINK.path ? SECONDARY_FALLBACK : SECONDARY_LINK;
  const hasOverview = intro.length > 0 || sections.length > 0;

  return (
    <main className={styles.innerPage}>
      {/* Page hero — paper, like every page's first surface. Two layouts: split
          (copy beside a photo) when the route has an honest image, stacked
          typography when it does not. .hero reserves the fixed header's height
          above the copy. */}
      <Section tone="paper" size="lg">
        <div
          className={`${styles.hero} ${heroImage ? styles.heroSplit : styles.heroStack}`}
        >
          <div className={styles.heroCopy}>
            {page.eyebrow ? (
              <Reveal as={MicroLabel} className={styles.heroEyebrow} direction="up">
                {page.eyebrow}
              </Reveal>
            ) : null}

            {/* The Reveal is a wrapper, not the h1 itself: Statement keeps its
                own element and the h1 stays free of animation classes. */}
            <Reveal direction="up" delay={60}>
              <Statement as="h1" size="h2" className={styles.heroTitle}>
                {page.title}
              </Statement>
            </Reveal>

            {page.subtitle ? (
              <Reveal as="p" className={styles.heroLead} direction="up" delay={120}>
                {page.subtitle}
              </Reveal>
            ) : null}

            {isUtility ? null : (
              <Reveal className={styles.heroActions} direction="up" delay={180}>
                {heroImage ? null : <hr className={`hairline ${styles.heroRule}`} />}
                <div className={styles.heroButtons}>
                  <Link to="/contact" className="btn">
                    {HERO_CTA_LABEL}
                    <Icon name="arrow-up-right" size={16} />
                  </Link>
                  <Link to={secondary.path} className="link-arrow">
                    {secondary.label}
                  </Link>
                </div>
              </Reveal>
            )}
          </div>

          {/* Unframed: no border, no scrim, no shadow — only the panel radius. */}
          {heroImage ? (
            <Reveal className={styles.heroMedia} direction="up" delay={120}>
              <img
                src={publicAsset(heroImage.src)}
                alt={heroImage.alt}
                className={styles.heroImage}
                decoding="async"
              />
            </Reveal>
          ) : null}
        </div>
      </Section>

      {/* Overview — the page's lede and its own sections. No VISIBLE
          SectionHead: the hero already titled the page, and a manufactured h2
          would be filler on all thirty routes.
          The h2 still has to exist, though — without it this block jumps from
          the page h1 straight to its row h3s, which is a skipped heading level
          (axe "heading-order") and costs screen-reader users the structure of
          long documents like the privacy policy. So it is present and read
          aloud, just not painted. */}
      {hasOverview ? (
        <Section tone="paper">
          <h2 className={styles.assistiveHeading}>Overview</h2>

          {intro.length > 0 ? (
            <Reveal className={styles.intro} direction="up">
              {intro.map((paragraph) => (
                <p key={paragraph} className={styles.introText}>
                  {paragraph}
                </p>
              ))}
            </Reveal>
          ) : null}

          {sections.length > 0 ? (
            <Ledger items={sections} headingKey="heading" bodyKey="body" />
          ) : null}
        </Section>
      ) : null}

      {page.team ? (
        <Section tone="paper">
          <SectionHead eyebrow={LABELS.team.eyebrow} title={LABELS.team.title} />
          <ul className={styles.teamGrid}>
            {page.team.map((member, index) => (
              <Reveal
                as="li"
                key={`${member.name || member.role}-${index}`}
                className={styles.teamCard}
                direction="up"
                delay={stagger(index)}
              >
                {/* Numbered rather than photographed — see the roster note in
                    ContentPage.module.css. aria-hidden because the index is a
                    visual ordering device, not information. */}
                <MicroLabel className={styles.teamIndex} aria-hidden="true">
                  {ordinal(index)}
                </MicroLabel>
                {member.name ? (
                  <h3 className={styles.teamName}>{member.name}</h3>
                ) : null}
                <MicroLabel as="p" className={styles.teamRole}>
                  {member.role}
                </MicroLabel>
                {member.bio ? <p className={styles.teamBio}>{member.bio}</p> : null}
              </Reveal>
            ))}
          </ul>
        </Section>
      ) : null}

      {page.serviceHighlights ? (
        <Section tone="paper">
          <SectionHead
            eyebrow={LABELS.highlights.eyebrow}
            title={LABELS.highlights.title}
          />
          <Columns count={page.serviceHighlights.length}>
            {page.serviceHighlights.map((item, index) => (
              <Reveal
                as="li"
                key={item.heading}
                className={styles.columnItem}
                direction="up"
                delay={stagger(index)}
              >
                <TicketCard tone="paper" className={styles.card}>
                  <MicroLabel className={styles.cardIndex} aria-hidden="true">
                    {ordinal(index)}
                  </MicroLabel>
                  <h3 className={styles.cardTitle}>{item.heading}</h3>
                  <p className={styles.cardBody}>{item.body}</p>
                </TicketCard>
              </Reveal>
            ))}
          </Columns>
        </Section>
      ) : null}

      {page.differentiators ? (
        <Section tone="paper">
          <SectionHead
            eyebrow={LABELS.differentiators.eyebrow}
            title={LABELS.differentiators.title}
          />
          <ul className={styles.pointList}>
            {page.differentiators.map((item, index) => (
              <Reveal
                as="li"
                key={item.title}
                className={styles.point}
                direction="up"
                delay={stagger(index)}
              >
                <Icon name="check" size={22} className={styles.pointIcon} />
                <div className={styles.pointBody}>
                  <h3 className={styles.pointTitle}>{item.title}</h3>
                  <p className={styles.pointText}>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* Process is the page's one dark block inside the body: an inset olive
          panel. The index is a bare numeral, not the word "Step": several pages
          already label their own steps ("Week 0", "Step 1 — ...") and a second
          label would read as a duplicate. It is a micro label ON OLIVE, which is
          one of the few places the lime accent is allowed (.micro--accent is
          lime on olive / void and ink on paper, by construction). */}
      {page.process ? (
        <Section tone="olive" panel>
          <SectionHead eyebrow={LABELS.process.eyebrow} title={LABELS.process.title} />
          <ol
            className={styles.steps}
            style={{ "--cols": Math.min(Math.max(page.process.length, 1), 4) }}
          >
            {page.process.map((step, index) => (
              <Reveal
                as="li"
                key={step.title}
                className={styles.step}
                direction="up"
                delay={stagger(index)}
              >
                <MicroLabel
                  className={`micro--accent ${styles.stepIndex}`}
                  aria-hidden="true"
                >
                  {ordinal(index)}
                </MicroLabel>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepBody}>{step.body}</p>
              </Reveal>
            ))}
          </ol>
        </Section>
      ) : null}

      {/* Technology — names only, set as type. No vendor logos: the previous
          version hotlinked 23 brand marks from two third-party CDNs, and
          substituting glyphs from our own icon set would misrepresent those
          brands. Same treatment as the homepage stack row. */}
      {page.technologyGroups ? (
        <Section tone="paper">
          <SectionHead
            eyebrow={LABELS.technology.eyebrow}
            title={LABELS.technology.title}
          />
          <ul className={styles.techGroups}>
            {page.technologyGroups.map((group, index) => (
              <Reveal
                as="li"
                key={group.label}
                className={styles.techGroup}
                direction="up"
                delay={stagger(index)}
              >
                <MicroLabel as="p" className={styles.techLabel} id={`tech-${index}`}>
                  {group.label}
                </MicroLabel>
                {/* role="list" is redundant per spec but load-bearing: Safari
                    drops list semantics once list-style is none, and with it the
                    "list, N items" announcement. */}
                {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                <ul
                  className={styles.techNames}
                  role="list"
                  aria-labelledby={`tech-${index}`}
                >
                  {(group.items || []).map((name) => (
                    <li key={name} className={styles.techName}>
                      {name}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </ul>
        </Section>
      ) : null}

      {page.pageFaqs ? (
        <Section tone="paper">
          <SectionHead eyebrow={LABELS.faqs.eyebrow} title={LABELS.faqs.title} />
          <Ledger items={page.pageFaqs} headingKey="question" bodyKey="answer" />
        </Section>
      ) : null}

      {related.length > 0 ? (
        <Section tone="paper">
          <SectionHead eyebrow={LABELS.related.eyebrow} title={LABELS.related.title} />
          <Columns count={related.length}>
            {related.map((item, index) => (
              <Reveal
                as="li"
                key={item.path}
                className={styles.columnItem}
                direction="up"
                delay={stagger(index)}
              >
                <TicketCard tone="paper" className={styles.card}>
                  {item.eyebrow ? (
                    <MicroLabel as="p" className={styles.cardTag}>
                      {item.eyebrow}
                    </MicroLabel>
                  ) : null}
                  <h3 className={styles.cardTitle}>{item.title}</h3>
                  {item.subtitle ? (
                    <p className={styles.cardBody}>{item.subtitle}</p>
                  ) : null}
                  {/* A .link-arrow, never a pill: this CTA repeats once per
                      card. The accessible name opens with the visible label
                      (WCAG 2.5.3) and then says where it goes. */}
                  <div className={styles.cardFoot}>
                    <Link
                      to={item.path}
                      className="link-arrow"
                      aria-label={`${RELATED_CTA_LABEL} ${item.title}`}
                    >
                      {RELATED_CTA_LABEL}
                    </Link>
                  </div>
                </TicketCard>
              </Reveal>
            ))}
          </Columns>
        </Section>
      ) : null}

      {/* Closing CTA — an inset olive panel. Real copy from
          SITE_CONTENT.finalCta, one lime pill, one text link. The title is data,
          so it is rendered as-is. Utility routes skip it entirely. */}
      {isUtility ? null : (
        <Section tone="olive" panel size="lg" container="narrow">
          <SectionHead
            align="center"
            eyebrow={LABELS.cta.eyebrow}
            title={SITE_CONTENT.finalCta.title}
            lead={SITE_CONTENT.finalCta.subtitle}
          />
          <Reveal className={styles.ctaActions} direction="up">
            <Link to="/contact" className="btn">
              {SITE_CONTENT.finalCta.button}
              <Icon name="arrow-up-right" size={16} />
            </Link>
            <Link to={secondary.path} className="link-arrow">
              {secondary.label}
            </Link>
          </Reveal>
          {SITE_CONTENT.finalCta.description ? (
            <Reveal as="p" className={styles.ctaNote} direction="up" delay={60}>
              {SITE_CONTENT.finalCta.description}
            </Reveal>
          ) : null}
        </Section>
      )}
    </main>
  );
}

export default ContentPage;
